import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  FaFlask, 
  FaMicroscope, 
  FaLeaf, 
  FaCertificate,
  FaCheckCircle,
  FaClipboardList,
  FaLightbulb,
  FaCogs,
  FaVial,
  FaIndustry,  
  FaShieldAlt,
  FaBoxOpen,
  FaBalanceScale,
  FaHandshake,
  FaRocket,
  FaClipboardCheck,
  FaDollarSign,
  FaChevronRight,
  FaSeedling,
  FaAppleAlt,
  FaWineBottle,
  FaPepperHot
} from 'react-icons/fa';
import { GiCoconuts, GiChemicalDrop, GiPowder, GiBottledBolt } from 'react-icons/gi';

/**
 * ═══════════════════════════════════════════════════════════════
 * 🔬 RESEARCH & DEVELOPMENT PAGE
 * ═══════════════════════════════════════════════════════════════
 * 
 * 🌍 MULTI-LANGUAGE SUPPORT:
 * Translation keys: rnd.*
 * 
 * 📋 SECTIONS:
 * 1. Hero introduction
 * 2. What We Do - Service categories
 * 3. Core Capabilities
 * 4. Development Workflow (6 steps)
 * 5. Focus Areas
 * 6. Example Projects
 * 7. Deliverables
 * 8. How We Work
 * 
 * ═══════════════════════════════════════════════════════════════
 */

const RND = () => {
  const { t } = useTranslation();

  const whatWeDo = [
    {
      icon: <FaAppleAlt />,
      title: t('rnd.whatWeDo.productDevelopment.title'),
      items: t('rnd.whatWeDo.productDevelopment.items', { returnObjects: true })
    },
    {
      icon: <FaRocket />,
      title: t('rnd.whatWeDo.lineExtensions.title'),
      items: t('rnd.whatWeDo.lineExtensions.items', { returnObjects: true })
    },
    {
      icon: <FaFlask />,
      title: t('rnd.whatWeDo.customFormulations.title'),
      items: t('rnd.whatWeDo.customFormulations.items', { returnObjects: true })
    },
    {
      icon: <FaCogs />,
      title: t('rnd.whatWeDo.processOptimization.title'),
      items: t('rnd.whatWeDo.processOptimization.items', { returnObjects: true })
    },
    {
      icon: <GiChemicalDrop />,
      title: t('rnd.whatWeDo.ingredientInnovation.title'),
      items: t('rnd.whatWeDo.ingredientInnovation.items', { returnObjects: true })
    },
    {
      icon: <FaLightbulb />,
      title: t('rnd.whatWeDo.rapidPrototyping.title'),
      items: t('rnd.whatWeDo.rapidPrototyping.items', { returnObjects: true })
    },
    {
      icon: <FaDollarSign />,
      title: t('rnd.whatWeDo.costingScaleUp.title'),
      items: t('rnd.whatWeDo.costingScaleUp.items', { returnObjects: true })
    }
  ];

  const capabilities = [
    {
      icon: <FaMicroscope />,
      title: t('rnd.capabilities.applicationsLab.title'),
      desc: t('rnd.capabilities.applicationsLab.description')
    },
    {
      icon: <GiPowder />,
      title: t('rnd.capabilities.millingGranulation.title'),
      desc: t('rnd.capabilities.millingGranulation.description')
    },
    {
      icon: <FaWineBottle />,
      title: t('rnd.capabilities.infusionsCoatings.title'),
      desc: t('rnd.capabilities.infusionsCoatings.description')
    },
    {
      icon: <GiBottledBolt />,
      title: t('rnd.capabilities.condimentsSauces.title'),
      desc: t('rnd.capabilities.condimentsSauces.description')
    },
    {
      icon: <FaBoxOpen />,
      title: t('rnd.capabilities.packaging.title'),
      desc: t('rnd.capabilities.packaging.description')
    },
    {
      icon: <FaVial />,
      title: t('rnd.capabilities.analytics.title'),
      desc: t('rnd.capabilities.analytics.description')
    },
    {
      icon: <FaShieldAlt />,
      title: t('rnd.capabilities.qualitySystems.title'),
      desc: t('rnd.capabilities.qualitySystems.description')
    }
  ];

  const workflow = [
    {
      step: t('rnd.workflow.step1.number'),
      title: t('rnd.workflow.step1.title'),
      desc: t('rnd.workflow.step1.description'),
      icon: <FaClipboardList />
    },
    {
      step: t('rnd.workflow.step2.number'),
      title: t('rnd.workflow.step2.title'),
      desc: t('rnd.workflow.step2.description'),
      icon: <FaClipboardCheck />
    },
    {
      step: t('rnd.workflow.step3.number'),
      title: t('rnd.workflow.step3.title'),
      desc: t('rnd.workflow.step3.description'),
      icon: <FaFlask />
    },
    {
      step: t('rnd.workflow.step4.number'),
      title: t('rnd.workflow.step4.title'),
      desc: t('rnd.workflow.step4.description'),
      icon: <FaIndustry />
    },
    {
      step: t('rnd.workflow.step5.number'),
      title: t('rnd.workflow.step5.title'),
      desc: t('rnd.workflow.step5.description'),
      icon: <FaCertificate />
    },
    {
      step: t('rnd.workflow.step6.number'),
      title: t('rnd.workflow.step6.title'),
      desc: t('rnd.workflow.step6.description'),
      icon: <FaRocket />
    }
  ];

  const focusAreas = [
    { 
      icon: <FaLeaf />, 
      title: t('rnd.focusAreas.cleanLabel.title'), 
      desc: t('rnd.focusAreas.cleanLabel.description')
    },
    { 
      icon: <FaSeedling />, 
      title: t('rnd.focusAreas.organic.title'), 
      desc: t('rnd.focusAreas.organic.description')
    },
    { 
      icon: <FaBalanceScale />, 
      title: t('rnd.focusAreas.nutritionalTargets.title'), 
      desc: t('rnd.focusAreas.nutritionalTargets.description')
    },
    { 
      icon: <GiCoconuts />, 
      title: t('rnd.focusAreas.sustainability.title'), 
      desc: t('rnd.focusAreas.sustainability.description')
    }
  ];

  const exampleProjects = [
    {
      icon: <GiCoconuts />,
      title: t('rnd.exampleProjects.coconutSmiles.title'),
      desc: t('rnd.exampleProjects.coconutSmiles.description')
    },
    {
      icon: <FaWineBottle />,
      title: t('rnd.exampleProjects.coconutAminos.title'),
      desc: t('rnd.exampleProjects.coconutAminos.description')
    },
    {
      icon: <FaPepperHot />,
      title: t('rnd.exampleProjects.organicSpices.title'),
      desc: t('rnd.exampleProjects.organicSpices.description')
    }
  ];

  const deliverables = t('rnd.deliverables.items', { returnObjects: true });
  const howWeWork = t('rnd.howWeWork.items', { returnObjects: true });

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Spacer for navbar */}
      <div className="h-2"></div>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto text-center"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block mb-6"
          >
            <span className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-full text-sm font-semibold shadow-lg">
              {t('rnd.hero.badge')}
            </span>
          </motion.div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
            <span className="text-gray-900 dark:text-white">{t('rnd.hero.title')}</span>
            <span className="text-green-600 dark:text-green-500">{t('rnd.hero.titleHighlight')}</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed max-w-5xl mx-auto">
            {t('rnd.hero.description')}
          </p>
        </motion.div>
      </section>

      {/* What We Do */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-7xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('rnd.whatWeDo.sectionTitle')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {whatWeDo.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-green-100 dark:border-green-900"
              >
                <div className="text-4xl text-green-600 dark:text-green-500 mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {item.title}
                </h3>
                <ul className="space-y-2">
                  {item.items.map((subItem, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                      <FaChevronRight className="text-green-600 dark:text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-sm">{subItem}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Core Capabilities */}
      <section className="container mx-auto px-4 py-16 bg-white/50 dark:bg-gray-800/50">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-7xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('rnd.capabilities.sectionTitle')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="text-5xl text-green-600 dark:text-green-500 mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Development Workflow */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('rnd.workflow.sectionTitle')}
          </h2>
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-green-600 to-emerald-600 transform -translate-x-1/2"></div>
            
            <div className="space-y-12">
              {workflow.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className={`flex flex-col lg:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  }`}
                >
                  {/* Content Card */}
                  <div className="flex-1 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-green-200 dark:border-green-900">
                    <div className="flex items-start gap-4">
                      <div className="text-4xl text-green-600 dark:text-green-500">
                        {item.icon}
                      </div>
                      <div>
                        <div className="text-3xl font-bold text-green-600 dark:text-green-500 mb-2">
                          {item.step}
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {item.title}
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Center Circle */}
                  <div className="hidden lg:block w-12 h-12 rounded-full bg-gradient-to-br from-green-600 to-emerald-600 shadow-lg flex-shrink-0 border-4 border-white dark:border-gray-900"></div>

                  {/* Spacer */}
                  <div className="hidden lg:block flex-1"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* Focus Areas */}
      <section className="container mx-auto px-4 py-16 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-gray-800 dark:to-gray-900">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('rnd.focusAreas.sectionTitle')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {focusAreas.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, rotate: 2 }}
                className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 text-center"
              >
                <div className="text-5xl text-green-600 dark:text-green-500 mb-4 flex justify-center">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 text-sm">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Example Projects */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-6xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('rnd.exampleProjects.sectionTitle')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {exampleProjects.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.05, y: -10 }}
                className="bg-gradient-to-br from-white to-green-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 border-green-200 dark:border-green-900"
              >
                <div className="text-6xl text-green-600 dark:text-green-500 mb-4 flex justify-center">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 text-center">
                  {item.title}
                </h3>
                <p className="text-gray-700 dark:text-gray-300 text-center leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Deliverables */}
      <section className="container mx-auto px-4 py-16 bg-white/50 dark:bg-gray-800/50">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('rnd.deliverables.sectionTitle')}
          </h2>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-700 dark:to-gray-800 rounded-2xl p-8 shadow-xl">
            <ul className="space-y-4">
              {deliverables.map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <FaCheckCircle className="text-green-600 dark:text-green-500 text-2xl mt-1 flex-shrink-0" />
                  <span className="text-lg text-gray-900 dark:text-white">
                    {item}
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* How We Work */}
      <section className="container mx-auto px-4 py-16">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 dark:text-white mb-12">
            {t('rnd.howWeWork.sectionTitle')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {howWeWork.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ scale: 1.05 }}
                className="bg-gradient-to-br from-green-600 to-emerald-600 text-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 text-center"
              >
                <FaHandshake className="text-5xl mb-4 mx-auto" />
                <p className="text-lg font-semibold leading-relaxed">
                  {item}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default RND;
